import { useState } from "react";
import { Controller } from "react-hook-form";
import { cn } from "helper/Utils";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { IoChevronDown } from "react-icons/io5";

const InputSelect = ({
  label,
  placeholder,
  disabled,
  name,
  control,
  options,
  value,
  onChange,
  defaultValue,
  className,
  listOpenClassName,
  listBoxOptionsHeight,
  searchable = false,
  ...prop
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOptions = !searchable ? options : options.filter((option) => option?.label.toLowerCase().includes(searchQuery?.toLowerCase()));

  return (
    <>
      {control ? (
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue}
          render={({ field }) => (
            <Listbox {...field} className="input-group relative w-full" value={field.value} onChange={field.onChange} {...prop}>
              <div>
                <ListboxButton
                  className={cn(
                    "relative flex flex-col items-start leading-none w-full rounded !bg-white/15 text-white/80",
                    "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25 text-sm leading-3",
                    "disabled:bg-white/5",
                    label ? "py-[13px] pr-8 pl-4" : "py-2 px-3",
                    className
                  )}
                  disabled={disabled}
                >
                  {" "}
                  {label && (
                    <label className={`pointer-events-none mb-[5px] text-[12px] transition-all duration-200 ease-in-out text-white/50 `}>
                      {label}
                    </label>
                  )}
                  {field.value ? field.value?.label : placeholder ? placeholder : "Select an option"}
                  {!disabled && (
                    <IoChevronDown
                      className={cn("group pointer-events-none size-4 absolute fill-white/60", label ? "top-6 right-2.5" : " right-1")}
                      aria-hidden="true"
                    />
                  )}
                </ListboxButton>
                <ListboxOptions
                  anchor="bottom"
                  transition
                  className={cn(
                    " w-[var(--button-width)] z-[1001] bg-body rounded-xl border border-white/5 p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none shadow shadow-body group",
                    "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0",
                    listOpenClassName
                  )}
                >
                  <div className="max-h-[300px] overflow-y-auto">
                    {searchable && (
                      <input
                        type="text"
                        placeholder={`Search ${label}`}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full py-2 px-3 mb-1 text-sm text-white bg-white/10 rounded-lg focus:outline-none"
                      />
                    )}

                    {filteredOptions.length > 0 ? (
                      filteredOptions.map((option) => (
                        <ListboxOption
                          key={option.value}
                          value={option}
                          className="text-white/60 bg-white/10 text-[12px] leading-6 my-[1px] flex cursor-default items-center gap-2 rounded py-1 px-3 data-[focus]:bg-white/20 data-[focus]:text-white group-data-[selected]:bg-white w-full"
                        >
                          {/* <CheckIcon className="invisible size-4 fill-white group-data-[selected]:visible" /> */}
                          {option.label}
                        </ListboxOption>
                      ))
                    ) : (
                      <div className="text-sm/6 text-white py-2 px-3">No results found</div>
                    )}
                  </div>
                </ListboxOptions>
              </div>
            </Listbox>
          )}
        />
      ) : (
        <Listbox className="input-group relative w-full" value={value} onChange={onChange}>
          <>
            <ListboxButton
              className={cn(
                "relative flex items-center leading-none w-full rounded !bg-white/15 text-white/80",
                "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25 text-sm leading-3",
                "disabled:bg-white/5",
                label ? "py-[13px] pr-8 pl-4" : "py-2 px-3",
                className
              )}
              disabled={disabled}
            >
              {" "}
              {label && (
                <label className={`pointer-events-none mb-[5px] text-[12px] transition-all duration-200 ease-in-out text-white/50 `}>{label}</label>
              )}
              {value ? value : defaultValue ? defaultValue.label : placeholder ? placeholder : "Select an option"}
              {!disabled && (
                <IoChevronDown
                  className={cn("group pointer-events-none size-4 absolute fill-white/60", label ? "top-6 right-2.5" : " right-1")}
                  aria-hidden="true"
                />
              )}
            </ListboxButton>
            <ListboxOptions
              transition
              anchor="bottom"
              className={cn(
                "w-[var(--button-width)] z-[1001] bg-body rounded-xl border border-white/5 p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none shadow shadow-body",
                "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0",
                listOpenClassName
              )}
            >
              <div className={cn("overflow-y-auto", listBoxOptionsHeight ? listBoxOptionsHeight : "max-h-[300px]")}>
                {searchable && (
                  <input
                    type="text"
                    placeholder={`Search ${label}`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full py-2 px-3 mb-1 text-sm text-white bg-white/10 rounded-lg focus:outline-none"
                  />
                )}

                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <ListboxOption
                      key={option.value}
                      value={option}
                      className="text-white/60 bg-white/10 text-[12px] leading-6 my-[1px] flex cursor-default items-center gap-2 rounded-lg px-3 select-none data-[focus]:bg-white/20 data-[focus]:text-white group-data-[selected]:bg-white w-full"
                    >
                      {/* <CheckIcon className="invisible size-4 fill-white group-data-[selected]:visible" /> */}
                      {option.label}
                    </ListboxOption>
                  ))
                ) : (
                  <div className="text-sm/6 text-white py-2 px-3">No results found</div>
                )}
              </div>
            </ListboxOptions>
          </>
        </Listbox>
      )}
    </>
  );
};

export default InputSelect;
